import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, UserCheck, Check, Copy, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import ImageUpload from "@/components/ImageUpload";
import { Field, Panel, DelBtn } from "@/pages/admin/adminShared";
import { fetchSiteContent } from "@/lib/siteContent";
import { EVENTS_PAGE_DEFAULTS } from "@/pages/Events";

function CheckinDialog({ event, open, onOpenChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (open && event) {
      setLoading(true);
      api.get(`/admin/events/${event.id}/attendees`).then((r) => setRows(r.data)).catch(() => setRows([])).finally(() => setLoading(false));
    }
  }, [open, event]);
  const toggle = async (r) => {
    try {
      const { data } = await api.post(`/admin/registrations/${r.id}/checkin`);
      setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, checked_in: data.checked_in } : x)));
    } catch (e) { toast.error("Could not update check-in"); }
  };
  const checkedCount = rows.filter((r) => r.checked_in).length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-navy-paper border-slate-800 max-w-lg" data-testid="checkin-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-white">Check-in · {event?.title}</DialogTitle>
        </DialogHeader>
        <div className="text-gold text-xs uppercase tracking-widest">{checkedCount} / {rows.length} checked in</div>
        <div className="max-h-[55vh] overflow-y-auto space-y-2 mt-2">
          {loading ? <p className="text-slate-500 text-sm py-6 text-center">Loading attendees…</p>
            : rows.length === 0 ? <p className="text-slate-500 text-sm py-6 text-center">No confirmed attendees yet.</p>
            : rows.map((r) => (
              <button key={r.id} onClick={() => toggle(r)} data-testid={`checkin-row-${r.id}`}
                className={`w-full flex items-center justify-between border p-3 transition-colors ${r.checked_in ? "border-accent bg-accent/10" : "border-slate-700 hover:border-gold"}`}>
                <span className="text-white text-sm">{r.player_name}</span>
                <span className={`flex items-center gap-1.5 text-xs uppercase tracking-widest ${r.checked_in ? "text-accent" : "text-slate-500"}`}>
                  {r.checked_in ? <><Check className="w-4 h-4" /> Checked in</> : "Tap to check in"}
                </span>
              </button>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReplicateDialog({ event, open, onOpenChange, onReplicate }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setDate(""); setTime(event?.time || ""); } }, [open, event]);
  const go = async () => {
    if (!date) { toast.error("Pick a date for the new event"); return; }
    if (date === event?.date && (time || "") === (event?.time || "")) { toast.error("New date and/or time must differ from the original"); return; }
    setBusy(true);
    const ok = await onReplicate(event.id, { date, time });
    setBusy(false);
    if (ok) onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-navy-paper border-slate-800 rounded-none" data-testid="replicate-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl text-white">Replicate · {event?.title}</DialogTitle></DialogHeader>
        <p className="text-slate-400 text-sm">Creates an identical event with a new date and/or time.</p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-slate-300 text-xs">New Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 rounded-none bg-navy border-slate-700 text-sm" data-testid="replicate-date" /></div>
          <div><Label className="text-slate-300 text-xs">New Time</Label><Input value={time} onChange={(e) => setTime(e.target.value)} placeholder="6:30 PM" className="mt-1 rounded-none bg-navy border-slate-700 text-sm" data-testid="replicate-time" /></div>
        </div>
        <DialogFooter>
          <Button onClick={go} disabled={busy} className="rounded-none bg-gold text-navy hover:bg-gold-light text-xs uppercase tracking-widest font-bold" data-testid="replicate-confirm">{busy ? "Creating…" : "Create Copy"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EventsPageTextEditor() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/content").then((r) => setContent(r.data || {})).catch(() => setContent({})); }, []);
  if (!content) return null;
  const page = { ...EVENTS_PAGE_DEFAULTS, ...(content.events || {}) };
  const set = (k, v) => setContent({ ...content, events: { ...page, [k]: v } });
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.get("/content");
      await api.put("/admin/content", { data: { ...(data || {}), events: page } });
      fetchSiteContent();
      toast.success("Events page text updated");
    } catch (e) { toast.error("Could not save"); } finally { setSaving(false); }
  };
  return (
    <div className="mt-6 bg-navy-paper border border-slate-800 p-5 space-y-3" data-testid="events-page-text-editor">
      <div className="text-gold text-xs uppercase tracking-[0.3em]">Events Page Heading</div>
      <Field label="Heading" value={page.title} onChange={(e) => set("title", e.target.value)} data-testid="events-page-title-input" />
      <div><Label className="text-slate-300 text-xs">Intro text</Label><Textarea rows={3} value={page.subtitle} onChange={(e) => set("subtitle", e.target.value)} className="mt-1 rounded-none bg-navy border-slate-700 text-sm" data-testid="events-page-subtitle-input" /></div>
      <Button onClick={save} disabled={saving} className="bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest text-xs" data-testid="events-page-text-save">{saving ? "Saving…" : "Save Heading"}</Button>
    </div>
  );
}

export default function EventsAdmin({ items, stats = {}, currentSeason, venues = [], seasons = [], blindStructures = [], onCreate, onUpdate, onDelete, onReplicate, onToggleVisibility }) {
  const empty = { title: "", subtitle: "", date: "", time: "", venue_name: "", address: "", venue_website: "", map_query: "", game_type: "", blind_structure: "", buy_in: 0, seat_price: 0, food: "", description: "", image: "", season: currentSeason || "", seats_total: 100, featured: false, status: "upcoming", visible: true };
  const [f, setF] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [checkin, setCheckin] = useState(null);
  const [replicate, setReplicate] = useState(null);
  const startEdit = (ev) => { setEditingId(ev.id); setF({ ...empty, ...ev }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const cancel = () => { setEditingId(null); setF(empty); };

  const pickVenue = (name) => {
    const v = venues.find((x) => x.name === name);
    setF((prev) => ({ ...prev, venue_name: name, address: v ? (v.address || "") : prev.address, venue_website: v ? (v.website || "") : prev.venue_website }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const body = { ...f, buy_in: Number(f.buy_in), seat_price: Number(f.seat_price), seats_total: Number(f.seats_total) };
    const ok = editingId ? await onUpdate(editingId, body) : await onCreate(body);
    if (ok) cancel();
  };

  const seasonNames = seasons.map((s) => s.name);

  return (
    <>
    <EventsPageTextEditor />
    <Panel title={editingId ? "Edit Event" : "New Event"} form={
      <form onSubmit={submit} className="space-y-3" data-testid="event-form">
        <Field label="Title" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <Field label="Second Title Line (optional)" value={f.subtitle || ""} onChange={(e) => setF({ ...f, subtitle: e.target.value })} placeholder="e.g. $20,000 Guaranteed" data-testid="event-subtitle-input" />
        <div className="grid grid-cols-2 gap-2">
          <Field label="Date" type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
          <Field label="Time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} placeholder="6:30 PM" />
        </div>
        <div>
          <Label className="text-slate-300 text-xs">Venue</Label>
          <select value={f.venue_name} onChange={(e) => pickVenue(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-none bg-navy border border-slate-700 text-white text-sm" data-testid="event-venue">
            <option value="">Select a venue…</option>
            {venues.map((v) => <option key={v.id} value={v.name}>{v.name}</option>)}
          </select>
        </div>
        <Field label="Address (auto-fills from venue)" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
        <Field label="Venue Website Link" value={f.venue_website} onChange={(e) => setF({ ...f, venue_website: e.target.value })} placeholder="https://… (optional)" />
        <Field label="Game Type" value={f.game_type} onChange={(e) => setF({ ...f, game_type: e.target.value })} />
        <div>
          <Label className="text-slate-300 text-xs">Blind Structure</Label>
          <select value={f.blind_structure} onChange={(e) => setF({ ...f, blind_structure: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-none bg-navy border border-slate-700 text-white text-sm" data-testid="event-blind-structure">
            <option value="">None</option>
            {blindStructures.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Buy-in $" type="number" value={f.buy_in} onChange={(e) => setF({ ...f, buy_in: e.target.value })} />
          <Field label="Seats" type="number" value={f.seats_total} onChange={(e) => setF({ ...f, seats_total: e.target.value })} />
        </div>
        <div>
          <Label className="text-slate-300 text-xs">Season</Label>
          <select value={f.season} onChange={(e) => setF({ ...f, season: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-none bg-navy border border-slate-700 text-white text-sm" data-testid="event-season">
            <option value="">Select a season…</option>
            {seasonNames.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <Field label="Food (leave blank to hide on event page)" value={f.food} onChange={(e) => setF({ ...f, food: e.target.value })} />
        <ImageUpload label="Event Image" value={f.image} onChange={(url) => setF({ ...f, image: url })} testId="event-image-upload" />
        <div><Label className="text-slate-300 text-xs">Description</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1 rounded-none bg-navy border-slate-700 text-sm" rows={3} /></div>
        <label className="flex items-center gap-2 text-slate-300 text-sm"><input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} /> Featured</label>
        <label className="flex items-center gap-2 text-slate-300 text-sm" data-testid="event-visible-toggle"><input type="checkbox" checked={f.visible !== false} onChange={(e) => setF({ ...f, visible: e.target.checked })} /> Show on live events page</label>
        <div className="flex gap-2">
          <Button type="submit" className="flex-1 bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest text-xs" data-testid="event-submit">{editingId ? "Save Changes" : "Add Event"}</Button>
          {editingId && <Button type="button" onClick={cancel} variant="outline" className="rounded-none border-slate-600 text-slate-300 text-xs" data-testid="event-cancel">Cancel</Button>}
        </div>
      </form>
    }>
      <div className="space-y-2">
        {items.map((e) => (
          <div key={e.id} className="bg-navy-paper border border-slate-800 p-4 flex items-center justify-between" data-testid={`admin-event-${e.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              {e.image && <img src={e.image.startsWith("http") ? e.image : `${process.env.REACT_APP_BACKEND_URL}${e.image}`} alt="" className="w-12 h-12 object-cover" />}
              <div className="min-w-0">
                <div className="text-white font-medium text-sm flex items-center gap-2">
                  {e.title}
                  {e.subtitle && <span className="text-gold/80 text-xs font-normal">— {e.subtitle}</span>}
                  {e.visible === false && <span className="text-slate-500 text-[9px] uppercase tracking-widest border border-slate-700 px-1.5 py-0.5" data-testid={`event-hidden-badge-${e.id}`}>Hidden</span>}
                </div>
                <div className="text-slate-500 text-xs truncate">{e.date} · {e.venue_name} · ${e.buy_in || 0} buy-in{stats[e.id] ? ` · ${stats[e.id].checked_in}/${stats[e.id].booked} checked in` : ""}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button onClick={() => onToggleVisibility(e.id, !(e.visible !== false))} className={`transition-colors ${e.visible !== false ? "text-accent hover:text-slate-400" : "text-slate-500 hover:text-accent"}`} title={e.visible !== false ? "Visible — click to hide" : "Hidden — click to show"} data-testid={`admin-visibility-event-${e.id}`}>
                {e.visible !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button onClick={() => setReplicate(e)} className="text-slate-500 hover:text-gold transition-colors" title="Replicate event" data-testid={`admin-replicate-event-${e.id}`}><Copy className="w-4 h-4" /></button>
              <button onClick={() => setCheckin(e)} className="text-slate-500 hover:text-accent transition-colors" title="Check-in list" data-testid={`admin-checkin-event-${e.id}`}><UserCheck className="w-4 h-4" /></button>
              <button onClick={() => startEdit(e)} className="text-slate-500 hover:text-gold transition-colors" data-testid={`admin-edit-event-${e.id}`}><Pencil className="w-4 h-4" /></button>
              <DelBtn onClick={() => onDelete(e.id)} />
            </div>
          </div>
        ))}
      </div>
      {checkin && <CheckinDialog event={checkin} open={!!checkin} onOpenChange={(v) => !v && setCheckin(null)} />}
      {replicate && <ReplicateDialog event={replicate} open={!!replicate} onOpenChange={(v) => !v && setReplicate(null)} onReplicate={onReplicate} />}
    </Panel>
    </>
  );
}
